import { GroupingOptionsV2 } from '@vae/server/src/utils/arrays/types/GroupingOptions'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../../../analytics/utils/firebaseUtils'
import { ConsumptionDataContext } from '../../../../../contexts/ConsumptionDataContext'
import useUser from '../../../../../contexts/hooks/useUser'
import { getMonthString, parseWeekDay } from '../utils/consumptionDataParser'

dayjs.extend(isSameOrAfter)

export type ChartTimeHook = ReturnType<typeof useChartTimeConsumptionChart>

export const useChartTimeConsumptionChart = () => {
	const { lastAvailableConsumptionData } = useContext(ConsumptionDataContext)

	const { user } = useUser()
	const { t } = useTranslation()

	const [time, setTime] = useState<dayjs.Dayjs>(
		lastAvailableConsumptionData?.isValid()
			? dayjs.tz(lastAvailableConsumptionData, 'Europe/Helsinki')
			: dayjs().tz('Europe/Helsinki')
	)
	const [span, setSpan] = useState<GroupingOptionsV2>('day')

	const panel = false

	const next = () => {
		logEvent('click-chart-time-next')
		setTime(value => value.add(1, span))
	}

	const back = () => {
		logEvent('click-chart-time-back')
		setTime(value => value.subtract(1, span))
	}

	useEffect(() => {
		if (!lastAvailableConsumptionData) return
		setTime(lastAvailableConsumptionData)
	}, [lastAvailableConsumptionData])

	const startTime = useMemo(() => {
		const processingSpan = span === 'week' ? 'isoWeek' : span
		if (span === 'hour') return time.startOf('day')
		return time.startOf(processingSpan)
	}, [span, time])

	const endTime = useMemo(() => {
		const processingSpan = span === 'week' ? 'isoWeek' : span
		let end = time.endOf(processingSpan)

		// Handle edge case for months with fewer than 31 days
		if (span === 'month' && end.date() !== time.daysInMonth()) {
			end = time.date(time.daysInMonth()).endOf('day')
		}
		if (span === 'hour') return time.endOf('day')

		return end
	}, [span, time])

	const differenceToPresentDay = useMemo(
		() => (time ? time.startOf('day').diff(dayjs().startOf('day'), 'days') : 0),
		[time]
	)

	const timeString = useMemo(() => {
		if (!time) return ''
		const stringFormat = 'DD.MM.YYYY'

		switch (span) {
			case 'day':
				switch (differenceToPresentDay) {
					case 1:
						return t('tomorrow')
					case 0:
						return t('today')
					case -1:
						return t('yesterday')
					default:
						return `${parseWeekDay(time.isoWeekday(), user?.language ?? 'fi')} ${time.format(stringFormat)}`
				}
			case 'week':
				if (panel && time.isAfter(dayjs(), 'isoWeek')) {
					return `${time.startOf('isoWeek').format(stringFormat)} - ${dayjs().format(stringFormat)}`
				} else {
					return `${time.startOf('isoWeek').format(stringFormat)} - ${time
						.endOf('isoWeek')
						.format(stringFormat)}`
				}
			case 'month':
				return getMonthString(time.month() + 1, user?.language ?? 'en').concat(' ' + time.year())
			case 'year':
				return time.format('YYYY')

			default:
				return ''
		}
		// eslint-disable-next-line
	}, [time, span, panel])

	return {
		dayjs: time,
		set: setTime,

		activeTimespan: span,
		setSpan: setSpan,

		next,
		back,

		differenceToPresentDay,

		startTime,
		endTime,

		timeString,
	}
}
